import { type ProcedureStep, ProcedureStepFieldValuesReviewType, type Stored } from "@overmap-ai/common"
import type { ReactNode } from "react"

import {
	ProcedureStepReviewStatusEqualsFilterModifier,
	ProcedureStepReviewStatusExcludesFilterModifier,
	ProcedureStepReviewStatusIncludesFilterModifier,
	ProcedureStepReviewStatusNotEqualsFilterModifier,
} from "../../modifiers/procedureStepReviewStatus"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { ProcedureStepReviewStatusFilterCell } from "./ProcedureStepReviewStatusFilterCell"

type ProcedureStepReviewStatusFieldValue = ProcedureStepFieldValuesReviewType | null
type ProcedureStepReviewStatusFilterValue =
	| ProcedureStepFieldValuesReviewType
	| null
	| (ProcedureStepFieldValuesReviewType | null)[]

export class ProcedureStepReviewStatusFilterDef extends BaseFilterDef<
	ProcedureStepReviewStatusFieldValue,
	ProcedureStepReviewStatusFilterValue
> {
	readonly id: string
	readonly defaultFilterValue: ProcedureStepReviewStatusFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly procedureStep: Stored<ProcedureStep>

	constructor(procedureStep: Stored<ProcedureStep>) {
		super()
		this.procedureStep = procedureStep
		this.id = `procedure-step-review-status:${procedureStep.uuid}`
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: ProcedureStepReviewStatusEqualsFilterModifier,
			isValueValid: (
				_value: ProcedureStepReviewStatusFieldValue,
			): _value is ProcedureStepReviewStatusFieldValue => true,
			isFilterValueValid: (
				filterValue: ProcedureStepReviewStatusFilterValue,
			): filterValue is ProcedureStepFieldValuesReviewType | null => !Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: ProcedureStepReviewStatusNotEqualsFilterModifier,
			isValueValid: (
				_value: ProcedureStepReviewStatusFieldValue,
			): _value is ProcedureStepReviewStatusFieldValue => true,
			isFilterValueValid: (
				filterValue: ProcedureStepReviewStatusFilterValue,
			): filterValue is ProcedureStepFieldValuesReviewType | null => !Array.isArray(filterValue),
		}),
		includes: createFilterModifierConfig({
			modifier: ProcedureStepReviewStatusIncludesFilterModifier,
			isValueValid: (
				_value: ProcedureStepReviewStatusFieldValue,
			): _value is ProcedureStepReviewStatusFieldValue => true,
			isFilterValueValid: (
				filterValue: ProcedureStepReviewStatusFilterValue,
			): filterValue is (ProcedureStepFieldValuesReviewType | null)[] => Array.isArray(filterValue),
		}),
		excludes: createFilterModifierConfig({
			modifier: ProcedureStepReviewStatusExcludesFilterModifier,
			isValueValid: (
				_value: ProcedureStepReviewStatusFieldValue,
			): _value is ProcedureStepReviewStatusFieldValue => true,
			isFilterValueValid: (
				filterValue: ProcedureStepReviewStatusFilterValue,
			): filterValue is (ProcedureStepFieldValuesReviewType | null)[] => Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<ProcedureStepReviewStatusFilterDef>): ReactNode {
		return <ProcedureStepReviewStatusFilterCell key={props.filter.id} {...props} />
	}
}
